import { useEffect, useState } from "react";
import { useRouter } from "next/router";

const BLACK = "Black";
const WHITE = "White";
const EMPTY_FILTERS = {category: ["men", "women", "unisex"], colour: [], colourTags: [], size: [], type: [], category: [] };

function hasValueActive(filters) {
  let hasValues = false;
  Object.keys(filters).map(key => {
    if (filters[key].length > 0) hasValues = true;
  });

  return true;
}

export const usePopulateFilters = (products, prevFilterValues) => {
  const router = useRouter();
  const [filteredValues, setFilteredValues] = useState(EMPTY_FILTERS);
  const [filterLabels, setFilterLabels] = useState({});
  const [sortBy, setSortBy] = useState("");

  const isFrench = router.locale === "fr";

  useEffect(() => {
    setFilters(products);
    if (prevFilterValues && !hasValueActive(prevFilterValues)) {
      setFilteredValues(EMPTY_FILTERS);
    }
    setSortBy("");
  }, [products]);

  const onChange = (values, label) => {
    setFilteredValues({
      ...filteredValues,
      [label]: Object.keys(values).filter((key) => values[key]),
    });
  };

  const clearFilters = () => {
    setFilteredValues(EMPTY_FILTERS);
    setSortBy("");
  };

  const setFilters = () => {
    let labels = {};
    let colourQty = [];
    if (products.length === 0) {
      setFilterLabels(labels);
      return;
    }

    const productsWithProductGroup = products.filter((product) => product.sanity);

    const setColourTags = () => {
      productsWithProductGroup.map((product) => {
        product.sanity?.shopifyProducts.forEach((product) => {
          const tags = (isFrench && product.colour?.tags_fr)
            ? product.colour?.tags_fr
            : product.colour?.tags;

          tags &&
            tags.map((tag) => {
              if (labels["colourTags"]) {
                labels["colourTags"].add(tag.value);
              } else {
                labels["colourTags"] = new Set();
                labels["colourTags"].add(tag.value);
              }
            });

          const colours = labels["colourTags"] ? [...labels["colourTags"]] : [];
          const sorted = colours.sort((a, b) => (a > b ? 1 : b > a ? -1 : 0));

          const hasBlack = sorted.includes(BLACK);
          const hasWhite = sorted.includes(WHITE);

          const customFirstColour = (colour) => {
            const removedColour = sorted.filter((item) => item !== colour);
            const withColourFirst = [colour, ...removedColour];
            const newColourSet = new Set(withColourFirst);
            labels["colourTags"] = newColourSet;

            setFilterLabels({
              ...filterLabels,
              colourTags: newColourSet,
              category: ["men", "women", "unisex"]
            });
          };

          const customFirstTwoColours = (colour1, colour2) => {
            const removedColours = sorted.filter((item) => item !== colour1 && item !== colour2);
            const withColoursFirst = [colour1, colour2, ...removedColours];
            const newColourSet = new Set(withColoursFirst);
            labels["colourTags"] = newColourSet;

            setFilterLabels({
              ...filterLabels,
              colourTags: newColourSet,
              category: ["men", "women", "unisex"]
            });
          };

          if (hasBlack && !hasWhite) customFirstColour(BLACK);
          if (!hasBlack && hasWhite) customFirstColour(WHITE);
          if (hasBlack && hasWhite) customFirstTwoColours(BLACK, WHITE);
          if (!hasBlack && !hasWhite) {
            const newColourSet = new Set(sorted);
            labels["colourTags"] = newColourSet;

            setFilterLabels({
              ...filterLabels,
              colourTags: newColourSet,
              category: ["men", "women", "unisex"]
            });
          }
        });
      });
    };

    products.map((product) => {
      product.variants.forEach((variant) => {
        // 1. adds color and size to labels
        if (variant.availableForSale && product.sanity) {
          // selectedOptions are the details of each variant
          variant.selectedOptions.forEach((option) => {
            const optionName = option.name.toLowerCase().match(/([Cc](o|ou)l(o|ou|eu)r)/g)
              ? "colour"
              : option.name.toLowerCase();

            if (optionName === "colour" || optionName === "size") {
              const optionValue = option.value.toLowerCase();

              // if the color option exists, add color quantity to total inventory
              // else just add with total inventory
              // want to show items with the most quantity first
              if (optionName === "colour") {
                const foundIndex = colourQty.findIndex((item) => item.colour === optionValue);
                if (foundIndex !== -1) {
                  colourQty[foundIndex] = {
                    colour: optionValue,
                    qty: product.totalInventory + colourQty[foundIndex].qty,
                  };
                } else {
                  colourQty.push({ colour: optionValue, qty: product.totalInventory });
                }
              }

              // adds new option to labels(color or size)
              if (labels[optionName]) {
                labels[optionName].add(optionValue.split('/')[0]);
              } else {
                labels[optionName] = new Set();
                labels[optionName].add(optionValue.split('/')[0]);
              }
            }
          });
        }
      });

      // 2. this is where type gets added to labels
      // color and size are above
      const typeEn = product.tags.find((tag) => tag.includes("type:"));
      const typeFr = product.tags.find((tag) => tag.includes("type-fr:"));
      const type = (isFrench && typeFr) ? typeFr : typeEn

      if (labels["type"]) {
        if (type) labels["type"].add(type);
      } else {
        labels["type"] = new Set();
        if (type) labels["type"].add(type);
      }
    });

    const sortBy = (key) => {
      return (a, b) => (a[key] > b[key] ? -1 : b[key] > a[key] ? 1 : 0);
    };

    const sorted = colourQty.concat().sort(sortBy("qty"));

    // 3. adds colour to labels
    labels["colour"] = new Set(sorted.map((item) => item.colour));

    if (labels["colour"] && labels["colour"].size < 1) {
      labels["colour"] = new Set();
    }
    if (labels["type"] && labels["type"].size < 1) {
      labels["type"] = new Set();
    }
    if (labels["size"] && labels["size"].size < 1) {
      labels["size"] = new Set();
    }

    // 4. adds colour tags to labels
    setColourTags();
    // 5. add labels to state
    setFilterLabels({ 
      ...labels,
      category: ["men", "women", "unisex"]
    });

  };

  return {
    setFilteredValues,
    filteredValues,
    filterLabels,
    sortBy,
    setSortBy,
    onChange,
    clearFilters,
  };
};
